import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { Alert, Button, Flex, Switch, Text } from '@nula/core-ui';
import type { RecordingSettings } from '../../hooks/use-recording-settings';
import styles from './detection-sources-section.module.css';

export type DetectionSourcesSectionProps = {
  /** App-based (mic-owner) detection — Slack/Zoom/Teams. No OS permission. */
  appDetectionEnabled: boolean;
  /** Browser-tab detection — Google Meet & links. macOS: needs Accessibility. */
  browserDetectionEnabled: boolean;
  onUpdate: (patch: Partial<RecordingSettings>) => void;
  /**
   * Optional interceptor for the browser switch. When set, takes precedence
   * over the plain `onUpdate({ browserDetectionEnabled })` so the parent can
   * gate on AX permission first (open the explainer before persisting).
   */
  onBrowserToggle?: (next: boolean) => void;
  /**
   * Whether macOS Accessibility permission is granted. Drives the inline
   * warning under the browser switch. Defaults to `true` so the warning never
   * flashes where AX is irrelevant.
   */
  axGranted?: boolean;
  /** Click handler for the "Open System Settings" button in the AX warning. */
  onOpenSystemSettings?: () => void;
  /**
   * True when AX permission was granted but the running process still reads
   * denied (macOS per-process cache) — an app restart is required. Escalates
   * the browser warning to a "Restart now" prompt instead of "Open System
   * Settings".
   */
  needsRestart?: boolean;
  /** Click handler for the "Restart now" button (relaunches the app). */
  onRestart?: () => void;
  /** Whether the host platform is macOS — Windows UIA needs no permission. */
  isMacOS?: boolean;
  className?: string;
};

const root = cva(styles.root);
const toggle = cva(styles.toggle);
const toggleLabel = cva(styles.toggleLabel);

/**
 * The two independent meeting-detection sources, each its own switch:
 *
 * - **App meetings** (mic-owner): Slack, Zoom, Teams. Needs no OS permission.
 * - **Browser meetings** (browser-tab): Google Meet & links. On macOS this
 *   needs Accessibility permission, so the AX warning Alert lives directly
 *   under this switch — the permission story belongs to browser detection
 *   alone, never to app detection.
 */
export function DetectionSourcesSection({
  appDetectionEnabled,
  browserDetectionEnabled,
  onUpdate,
  onBrowserToggle,
  axGranted = true,
  onOpenSystemSettings,
  needsRestart = false,
  onRestart,
  isMacOS = false,
  className,
}: DetectionSourcesSectionProps) {
  // The AX warning belongs to the browser switch: only on macOS, only when
  // browser detection is ON, and only when AX is explicitly denied.
  const showAxWarning = isMacOS && browserDetectionEnabled && axGranted === false;
  // When the grant is stuck behind the per-process cache, escalate to a
  // restart prompt instead of the "Open System Settings" path.
  const showRestart = showAxWarning && needsRestart;

  const handleBrowserToggle = (next: boolean) => {
    if (onBrowserToggle) {
      onBrowserToggle(next);
    } else {
      onUpdate({ browserDetectionEnabled: next });
    }
  };

  return (
    <Flex vertical className={clsx(root(), className)}>
      <Flex className={toggle()}>
        <Flex vertical className={toggleLabel()}>
          <Text size="sm" className={styles.label}>Detect app meetings</Text>
          <Text type="secondary" size="xs">
            Prompt to record when a meeting starts in Slack, Zoom, and Teams.
          </Text>
        </Flex>
        <Switch
          checked={appDetectionEnabled}
          onChange={(v) => onUpdate({ appDetectionEnabled: v })}
          size="sm"
        />
      </Flex>

      <Flex className={toggle()}>
        <Flex vertical className={toggleLabel()}>
          <Text size="sm" className={styles.label}>Detect browser meetings</Text>
          <Text type="secondary" size="xs">
            Prompt to record Google Meet and meeting links open in your browser.
          </Text>
        </Flex>
        <Switch
          checked={browserDetectionEnabled}
          onChange={handleBrowserToggle}
          size="sm"
        />
      </Flex>

      {showRestart ? (
        <Alert
          type="warning"
          showIcon
          message="Restart Nula to finish setup"
          description="You granted Accessibility, but macOS only applies it on a fresh launch. App detection (Slack, Zoom, Teams) is unaffected."
          action={
            onRestart ? (
              <Button size="sm" variant="primary" onClick={onRestart}>
                Restart now
              </Button>
            ) : undefined
          }
        />
      ) : showAxWarning ? (
        <Alert
          type="warning"
          showIcon
          message="Browser-meeting detection paused"
          description="Accessibility permission required. App detection (Slack, Zoom, Teams) still runs."
          action={
            onOpenSystemSettings ? (
              <Button size="sm" variant="default" onClick={onOpenSystemSettings}>
                Open System Settings
              </Button>
            ) : undefined
          }
        />
      ) : null}
    </Flex>
  );
}
